/**
 * Bot configuration schemas
 */
import { z } from "zod";
import { createEntityFilterSchema, CurrencyCodeSchema, ExchangeTypeSchema, NestedPaginationParamsSchema, PopulateQueryParamsSchema, PublicationStateSchema, SortParamsSchema, StrapiEntitySchema, StrapiIdSchema, StrapiMediaSchema, UserConfigSchema, } from "./common.schemas";
import { BotConfigRelationSchema, ExchangeSchema, LazyBotWarningRelation, LazyChargeRelation, LazyExchangeAccountRelation, LazyRefundRelation, LazySubscriptionRelation, LazyTradeRelation, LazyUserRelation, } from "./relations";
// ============================================================================
// Enums
// ============================================================================
// BotRiskSchema and ExchangeTypeSchema are imported from common.schemas.ts
/**
 * Bot status schema
 */
export const BotStatusSchema = z.enum([
    "ACTIVE",
    "PAUSED",
    "NOT_CREATED",
    "NOT_CONNECTED",
    "PAYMENT_REQUIRED",
    "NOT_VERIFIED",
    "TOO_LOW_LEVERAGE",
    "INVALID_ACCOUNT_TYPE",
    "VERIFYING",
    "ERROR",
]);
/**
 * Bot strategy type schema
 */
export const BotStrategyTypeSchema = z.union([
    z.enum(["PaperFX", "SteadyFX", "BalanceFX", "MaxiFX", "CustomFX"]),
    z.string(),
]);
/**
 * Account movement type schema
 */
export const AccountMovementTypeSchema = z.union([
    z.enum(["DEAL_TYPE_BALANCE", "DEAL_TYPE_CREDIT", "DEAL_TYPE_CHARGE"]),
    z.string(),
]);
/**
 * Billing period schema
 */
export const BillingPeriodSchema = z.enum(["monthly", "quarterly", "yearly", "one_time"]);
// ExchangePlatformSchema is imported from common.schemas.ts
// ============================================================================
// Input Schemas
// ============================================================================
/**
 * Bot config create input schema
 */
export const BotConfigCreateInputSchema = z
    .object({
    exchange_account: StrapiIdSchema,
    strategy: StrapiIdSchema,
    subscription: StrapiIdSchema.optional(),
    userConfig: UserConfigSchema.optional(),
    disabled: z.boolean().optional(),
    subscription_paymentMethod: StrapiIdSchema.optional(),
})
    .strict();
/**
 * User config input schema for updates
 * Uses the nested UserConfigSchema with advancedStopLoss
 */
export const UserConfigInputSchema = UserConfigSchema;
/**
 * Bot config update input schema
 */
export const BotConfigUpdateInputSchema = z
    .object({
    strategy: StrapiIdSchema.optional(),
    disabled: z.boolean().optional(),
    userConfig: UserConfigInputSchema.optional(),
})
    .strict();
/**
 * Bot config filters schema - typesafe filters for bot config queries
 * Created using createEntityFilterSchema to reuse field types from BotConfigRelationSchema
 */
export const BotConfigFiltersSchema = createEntityFilterSchema(BotConfigRelationSchema, {
    scalars: ["uuid", "disabled"],
    relations: ["exchange_account", "strategy", "subscription", "user", "stats"],
});
/**
 * Bot config query params schema
 * Accepts nested pagination format from frontend: { pagination: { page, pageSize } }
 */
export const BotConfigQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .merge(PopulateQueryParamsSchema)
    .extend({
    filters: BotConfigFiltersSchema.optional(),
    publicationState: PublicationStateSchema.optional(),
    // Optional server-side point budget for history-only small-card requests.
    historyMaxPoints: z.number().int().min(16).max(512).optional(),
    historySince: z.number().int().nonnegative().max(8640000000000000).optional(),
})
    .strict();
/**
 * Strategy input schema
 */
export const StrategyInputSchema = z
    .object({
    name: z.string().min(1, "Strategy name is required"),
    uuid: z.string().optional(),
    exchangeType: ExchangeTypeSchema.optional(),
    exclusive: z.boolean().optional(),
    exchanges: z.array(StrapiIdSchema).optional(),
    logo: StrapiIdSchema.optional(),
    images: z.array(StrapiIdSchema).optional(),
    banner: StrapiIdSchema.optional(),
})
    .strict();
/**
 * Strategy query params schema
 * Accepts nested pagination format from frontend: { pagination: { page, pageSize } }
 */
export const StrategyQueryParamsSchema = NestedPaginationParamsSchema.merge(SortParamsSchema)
    .merge(PopulateQueryParamsSchema)
    .extend({
    publicationState: z.enum(["live", "preview"]).optional(),
    filters: z
        .object({
        uuid: z.object({ $eq: z.string().optional() }).optional(),
        exchangeType: z.object({ $eq: ExchangeTypeSchema.optional() }).optional(),
    })
        .optional(),
})
    .strict();
// ============================================================================
// Component Schemas
// ============================================================================
/**
 * Performance point schema
 * Used by both:
 * - getUserAccumulatedProfits: returns { timestamp, profit }
 * - getStatisticsByConfig: returns { timestamp, absoluteProfit }
 */
export const PerformancePointSchema = z.object({
    timestamp: z.number(),
    profit: z.number().nullish(),
    absoluteProfit: z.number().nullish(),
    drawdown: z.number().nullish(),
});
/**
 * Day stats schema
 * Note: drawdown fields can be null when no data available
 */
export const DayStatsSchema = z.object({
    profit: z.number(),
    avgDrawdown: z.number().nullish(),
    maxDrawdown: z.number().nullish(),
    minDrawdown: z.number().nullish(),
});
/**
 * Month stats schema
 * Note: drawdown/balance fields can be null when no data available
 */
export const MonthStatsSchema = z.object({
    profit: z.number(),
    avgDrawdown: z.number().nullish(),
    maxDrawdown: z.number().nullish(),
    minDrawdown: z.number().nullish(),
    maxBalance: z.number().nullish(),
    days: z.record(DayStatsSchema).nullish(),
});
/**
 * Year stats schema
 * Note: drawdown/balance fields can be null when no data available
 */
export const YearStatsSchema = z.object({
    profit: z.number(),
    avgDrawdown: z.number().nullish(),
    maxDrawdown: z.number().nullish(),
    minDrawdown: z.number().nullish(),
    maxBalance: z.number().nullish(),
    months: z.record(MonthStatsSchema).nullish(),
});
/**
 * Periodical stats schema
 */
export const PeriodicalStatsSchema = z.record(YearStatsSchema);
/**
 * Bot stats schema - matches misc.bot-config-stats component
 */
export const BotStatsSchema = z.object({
    username: z.string().nullish(),
    platform: z.string().nullish(),
    baseCurrency: CurrencyCodeSchema.nullish(),
    contractSize: z.string().nullish(),
    leverage: z.string().nullish(),
    drawdown: z.number().nullish(),
    profit_percentage: z.number().nullish(),
    absolute_profit: z.number().nullish(),
    trades: z.number().nullish(),
    openTrades: z.number().nullish(),
    balance: z.number().nullish(),
    equity: z.number().nullish(),
    stopLoss: z.number().nullish(),
    timestamp: z.string().nullish(),
    status: z.string().nullish(),
    expected_profit: z.number().nullish(),
});
/**
 * Bot statistics schema - dynamically populated by extend-populate middleware
 * Not a Strapi entity, just computed statistics data
 */
export const BotStatisticsSchema = z.object({
    // Recent profit calculations from getStatsRecentProfits
    lastDayProfit: z.number().nullish(),
    lastWeekProfit: z.number().nullish(),
    lastMonthProfit: z.number().nullish(),
    lastYearProfit: z.number().nullish(),
    // Populated when statistics.history is requested
    history: z.array(PerformancePointSchema).nullish(),
    // Populated when statistics.periodicalStats is requested
    periodicalStats: PeriodicalStatsSchema.nullish(),
});
/**
 * Account movement schema
 * Note: API returns `time` (number, milliseconds) not `timestamp` (string)
 */
export const AccountMovementSchema = StrapiEntitySchema.extend({
    type: AccountMovementTypeSchema,
    amount: z.number(),
    time: z.number(),
    comment: z.string().nullish(),
});
// UserConfigSchema is imported from common.schemas.ts
// ExchangeSchema, ExchangeServerSchema, TradingAssetSchema, LeverageOptionsConstraintSchema
// are imported from relations.ts to avoid circular dependencies
// ============================================================================
// Entity Schemas
// ============================================================================
/**
 * Pricing model schema
 */
export const PricingModelSchema = StrapiEntitySchema.extend({
    name: z.string().nullish(),
    price: z.number().nullish(),
    currency: CurrencyCodeSchema.nullish(),
    billingPeriod: BillingPeriodSchema.nullish(),
    trialDays: z.number().nullish(),
    features: z.array(z.string()).nullish(),
});
/**
 * Strategy localization schema
 */
export const StrategyLocalizationSchema = z.object({
    id: StrapiIdSchema,
    locale: z.string(),
    name: z.string().nullish(),
    description: z.string().nullish(),
    shortDescription: z.string().nullish(),
});
/**
 * Strategy asset schema
 */
export const StrategyAssetSchema = StrapiEntitySchema.extend({
    symbol: z.string(),
    name: z.string().nullish(),
});
/**
 * Strategy details schema
 */
export const StrategyDetailsSchema = z.object({
    description: z.string().nullish(),
    shortDescription: z.string().nullish(),
    minBalance: z.number().nullish(),
    maxBalance: z.number().nullish(),
    minLeverage: z.number().nullish(),
});
/**
 * Strategy config options schema - JSON configuration for strategy settings
 */
export const StrategyConfigOptionsSchema = z.record(z.string(), z.unknown());
/**
 * Strategy schema - matches Strapi content type
 */
export const StrategySchema = StrapiEntitySchema.extend({
    name: z.string(),
    uuid: z.string().nullish(),
    exchangeType: ExchangeTypeSchema.nullish(),
    exclusive: z.boolean().nullish(),
    details: StrategyDetailsSchema.nullish(),
    configOptions: StrategyConfigOptionsSchema.nullish(),
    // Relations (populated)
    exchanges: z.array(ExchangeSchema).nullish(),
    logo: StrapiMediaSchema.nullish(),
    images: z.array(StrapiMediaSchema).nullish(),
    banner: StrapiMediaSchema.nullish(),
    pricingModels: z.array(PricingModelSchema).nullish(),
    owner: z.object({ id: StrapiIdSchema }).nullish(),
    assets: z.array(StrategyAssetSchema).nullish(),
    localizations: z.array(StrategyLocalizationSchema).nullish(),
    signals: z
        .array(StrapiEntitySchema.extend({
        type: z.string().nullish(),
        message: z.string().nullish(),
        data: z.record(z.string(), z.unknown()).nullish(),
    }))
        .nullish(),
});
/**
 * Bot config schema - extends BotConfigRelationSchema with relations
 */
export const BotConfigSchema = BotConfigRelationSchema.extend({
    // JSON fields from Strapi content type
    userConfig: UserConfigSchema.nullish(),
    // Dynamic fields populated by extend-populate middleware
    history: z.array(PerformancePointSchema).nullish(),
    statistics: BotStatisticsSchema.nullish(),
    accountMovements: z.array(AccountMovementSchema).nullish(),
    // Relations (populated) - using lazy schemas to break circular deps
    exchange_account: LazyExchangeAccountRelation.nullish(),
    strategy: StrategySchema.nullish(),
    subscription: LazySubscriptionRelation.nullish(),
    trades: z.array(LazyTradeRelation).nullish(),
    charges: z.array(LazyChargeRelation).nullish(),
    warnings: z.array(LazyBotWarningRelation).nullish(),
    refunds: z.array(LazyRefundRelation).nullish(),
    user: LazyUserRelation.nullish(),
});
/**
 * Currency stats item schema - stats for a single currency
 * Matches the per-currency stats returned by getUserGeneralStats
 */
export const CurrencyStatsItemSchema = z.object({
    main: z.boolean().nullish(),
    profit: z.number().nullish(),
    activeBots: z.number().nullish(),
    activeOpenTrades: z.number().nullish(),
    activeBalance: z.number().nullish(),
    activeEquity: z.number().nullish(),
    deposits: z.number().nullish(),
    withdrawals: z.number().nullish(),
    history: z.array(PerformancePointSchema).nullish(),
});
/**
 * General stats totals schema - aggregated totals across all currencies
 */
export const GeneralStatsTotalsSchema = z.object({
    mainCurrency: CurrencyCodeSchema,
    hasMultipleCurrencies: z.boolean(),
    balance: z.number(),
    equity: z.number(),
    deposits: z.number(),
    withdrawals: z.number(),
    activeBots: z.number(),
    openTrades: z.number(),
    conversionFailed: z.boolean().nullish(),
});
/**
 * User general stats v2 schema - the v2 format with perCurrency and totals
 * Used by getUserGeneralStats and getMyGeneralStatsPerCurrency endpoints
 */
export const UserGeneralStatsV2Schema = z.object({
    perCurrency: z.record(z.string(), CurrencyStatsItemSchema),
    totals: GeneralStatsTotalsSchema,
});
/**
 * General stats schema (legacy format - for backwards compatibility)
 */
export const GeneralStatsSchema = z.object({
    performanceHistory: z.array(PerformancePointSchema),
    profit: z.number(),
    activeBots: z.number(),
    activeEquity: z.number(),
    activeBalance: z.number(),
    activeOpenTrades: z.number(),
    currencyCode: CurrencyCodeSchema,
});
/**
 * Currency general stats schema (legacy format)
 * Direct record mapping currency codes to stats - used by legacy endpoints
 */
export const CurrencyGeneralStatsSchema = z.record(CurrencyCodeSchema, GeneralStatsSchema);
